import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import BASE_URL from "../constent";
import imageCompression from "browser-image-compression";

/* =========================
   Fetch All Shop Brands
========================= */
export const fetchAllShopBrands = createAsyncThunk(
  "shopBrand/fetchAllShopBrands",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/shopBrand`);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return [];
      }
      return rejectWithValue(
        error.response?.data || "Failed to load shop brands"
      );
    }
  }
);

/* =========================
   Add Shop Brand
========================= */
export const addShopBrand = createAsyncThunk(
  "shopBrand/addShopBrand",
  async ({ data }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) throw new Error("No token found");

      const formData = new FormData();
      formData.append("title_en", data.nameEn);
      formData.append("title_ar", data.nameAr);
      formData.append("link", data.websitelink);

      if (data.image instanceof File) {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1024,
          useWebWorker: true,
          fileType: "image/webp",
        };

        const compressedFile = await imageCompression(data.image, options);
        formData.append(
          "image",
          compressedFile,
          `shop-brand-${Date.now()}.webp`
        );
      }

      const response = await axios.post(`${BASE_URL}/shopBrand`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          token,
        },
      });

      return response.data.data;
    } catch (error) {
      console.error(
        "Add ShopBrand failed:",
        error.message,
        error.response?.data
      );
      return rejectWithValue(
        error.response?.data || "Failed to add Shop Brand"
      );
    }
  }
);

/* =========================
   Delete Shop Brand
========================= */
export const deleteShopBrandById = createAsyncThunk(
  "shopBrand/deleteShopBrandById",
  async (id, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) throw new Error("No token found");
      await axios.delete(`${BASE_URL}/shopBrand/${id}`, {
        headers: { token },
      });

      return id;
    } catch (error) {
      console.error(
        "Delete ShopBrand failed:",
        error.message,
        error.response?.data
      );
      return rejectWithValue(
        error.response?.data || "Failed to delete Shop Brand"
      );
    }
  }
);

/* =========================
   Update Shop Brand
========================= */
export const updateShopBrandById = createAsyncThunk(
  "shopBrand/updateShopBrandById",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) throw new Error("No token found");

      const formData = new FormData();
      formData.append("title_en", data.nameEn);
      formData.append("title_ar", data.nameAr);
      formData.append("link", data.link);

      if (data.image instanceof File) {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1024,
          useWebWorker: true,
          fileType: "image/webp",
        };

        const compressedFile = await imageCompression(data.image, options);
        formData.append(
          "image",
          compressedFile,
          `shop-brand-${Date.now()}.webp`
        );
      }

      const response = await axios.put(
        `${BASE_URL}/shopBrand/${id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            token,
          },
        }
      );

      return response.data.updatedShopBrand || response.data;
    } catch (error) {
      console.error(
        "Update ShopBrand failed:",
        error.message,
        error.response?.data
      );
      return rejectWithValue(
        error.response?.data || "Failed to update Shop Brand"
      );
    }
  }
);

/* =========================
   Slice
========================= */
const shopBrandSlice = createSlice({
  name: "shopBrand",
  initialState: {
    allShopBrands: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllShopBrands.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllShopBrands.fulfilled, (state, action) => {
        state.loading = false;
        state.allShopBrands = action.payload;
      })
      .addCase(fetchAllShopBrands.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
      })

      // Optional optimistic updates
      .addCase(addShopBrand.fulfilled, (state, action) => {
        state.allShopBrands.push(action.payload);
      })
      .addCase(deleteShopBrandById.fulfilled, (state, action) => {
        state.allShopBrands = state.allShopBrands.filter(
          (item) => item.id !== action.payload
        );
      })
      .addCase(updateShopBrandById.fulfilled, (state, action) => {
        const index = state.allShopBrands.findIndex(
          (item) => item.id === action.payload.id
        );
        if (index !== -1) {
          state.allShopBrands[index] = action.payload;
        }
      });
  },
});

export default shopBrandSlice.reducer;
